
import { IPatch } from '../utilities/Graft';

/**
 * @public
 */
export interface IFacet<TValue> {
  readonly id: string;
  readonly type: TValue;
}

/**
 * Represents a faceted container of multiple facets whose
 * values all share a common interface.
 * @public
 */
export interface IFaceted<TCommonFacet = {}> {
  readonly __usePackOrMergeToCreateAFacetedObject: TCommonFacet;
  [facetId: string]: TCommonFacet;
}

/**
 * Represents a mapping of temporary keys to facet types.
 * @public
 */
export type IFacetMapping<TFacets extends object, TCommonFacet = {}> = {
  [P in keyof TFacets]: IFacet<TFacets[P] & TCommonFacet>;
};

let lastId = 0;

/**
 * Defines an available facet.
 * A facet has an auto-incremented identifier for indexing, and a type for compiler-safety.
 *
 * @public
 */
export class Facet<TValue> implements IFacet<TValue> {
  private static _facetsById: {
    [id: string]: Facet<{}>;
  } = {};

  /**
   * The auto-incremented identifier for this facet definition.
   */
  public readonly id: string;

  /**
   * A name assigned to this Facet for debugging.
   * This value should *only* be used for diagnostics and debugging, and never used for
   * reading or writing facets on objects.
   */
  public readonly debugName: string;

  /**
   * This field has no runtime significance.
   * Use `typeof facet.type` to use the typing information.
   */
  public readonly type!: TValue;

  public static get(id: string): Facet<{}> {
    if (!id) {
      throw new Error('Unable to determine the Facet for an empty or undefined id.');
    }

    const facet = Facet._facetsById[id];

    if (!facet) {
      throw new Error(
        `Unable to determine the Facet for id '${id}'. ` +
        'Only store objects into a facet collection that have been created using the pack function. ' +
        'This error will typically occur if multiple versions of the @ms/items-view package have been bundled into the application.'
      );
    }

    return facet;
  }

  constructor(debugName: string) {
    this.debugName = debugName;

    this.id = getNextId(debugName);

    Facet._facetsById[this.id] = this;
  }

  /**
   * Unpacks the value of this facet from a faceted object.
   */
  public unpack<TCommonFacet = {}>(
    this: Facet<TCommonFacet>,
    faceted: IFaceted<TCommonFacet>): (TValue & TCommonFacet) | undefined {
    return <TValue & TCommonFacet>faceted[this.id];
  }

  /**
   * Packs the given value into a faceted object containing only this facet.
   */
  public pack(value: TValue | undefined): IFaceted<TValue> {
    return {
      [this.id]: value
    } as IFaceted<TValue>;
  }
}

/**
 * Unpacks mapped facets from an entity.
 *
 * @public
 */
export function unpack<TFacets extends object, TCommonFacet = {}>(
  mapping: IFacetMapping<TFacets, TCommonFacet>,
  entity: IFaceted<TCommonFacet>): TFacets {
  const facets = <TFacets>{};

  for (const key in mapping) {
    if (mapping.hasOwnProperty(key)) {
      const facet = mapping[key];

      // tslint:disable-next-line:no-any
      facets[key] = entity[facet.id] as any; // TODO File a bug on TypeScript
    }
  }

  return facets;
}

/**
 * Packs mapped facets into an output entity.
 *
 * @public
 */
export function pack<TFacets extends object, TCommonFacet = {}>(
  mapping: IFacetMapping<TFacets, TCommonFacet>,
  facets: TFacets): IFaceted<TCommonFacet>;
/**
 * @public
 */
export function pack<TFacets extends object, TCommonFacet extends object = object>(
  mapping: IFacetMapping<TFacets, IPatch<TCommonFacet>>,
  facets: IPatch<TFacets>): IPatch<IFaceted<TCommonFacet>>;
/**
 * @internal
 */
export function pack<TFacets extends object, TCommonFacet = {}>(
  mapping: IFacetMapping<TFacets, TCommonFacet>,
  facets: TFacets): IFaceted<TCommonFacet> {
  const entity = {} as IFaceted<TCommonFacet>;

  for (const key in mapping) {
    if (mapping.hasOwnProperty(key)) {
      const facet = mapping[key];

      // tslint:disable-next-line:no-any
      entity[facet.id] = facets[key] as any; // TODO File a bug on TypeScript
    }
  }

  return entity;
}

/**
 * @internal
 * @deprecated Use `...` syntax instead.
 */
export function merge<TCommonFacet = {}>(entities: IFaceted<TCommonFacet>[]): IFaceted<TCommonFacet> {
  const entity = {} as IFaceted<TCommonFacet>;

  for (const facets of entities) {
    for (const key in facets) {
      if (Object.prototype.hasOwnProperty.call(facets, key)) {
        entity[key] = facets[key];
      }
    }
  }

  return entity;
}

/**
 * Creates a 'dynamic' facet, which can create derived facets which all share the same unique prefix.
 * @deprecated Do not use this. Instead, use a facet whose value maps keys to other item keys, and
 * store additional values as facets on those items.
 * @beta
 */
export function createDynamicFacet(name: string): <TValue>(id: string) => IFacet<TValue> {
  const facet = new Facet<{}>(name);

  return <TValue>(id: string): IFacet<TValue> => {
    return {
      id: `${facet.id}_${id}`
    } as IFacet<TValue>;
  };
}

/**
 * @public
*/
export function getNextId(debugName: string): string {
  return `${debugName}_${++lastId}`;
}
